import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';

import { Schedule } from 'src/providers/schedule/schedule';
import { OwnerBlocksService } from 'src/shared/services/owner-blocks.service';
import { DemoBlockService } from 'src/shared/services/demo-block.service';
import { WindowService } from 'src/shared/providers/window.service';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';

import { AddOwnerBlockPage, OWNER_BLOCK_EDIT_KEY, OwnerBlockEditContext } from 'src/pages/add-owner-block/add-owner-block';

import { Util } from 'src/shared/util/util';

import { EscapeHtmlPipe } from 'src/shared/pipes/escape-html.pipe';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';

import { OwnerBlock } from 'src/models/schedule-weekly-view.model';

export type OwnerBlockCardSize = 'sm' | 'md';
export type OwnerBlockCardVariant = 'card' | 'bar' | 'row';

/**
 * A blocked-dates bar for the schedule calendars. Reads as unavailability
 * rather than an event: hatched, muted, with a lock badge. Hover (desktop)
 * or tap opens a popover with the details and an Unblock action.
 * The `bar` variant is the slim pill the monthly view's swim lanes use, and
 * `row` is a line in the monthly view's list of a day's blocks, opened by click.
 */
@Component({
  selector: 'app-owner-block-card',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './owner-block-card.component.html',
  styleUrls: ['./owner-block-card.component.scss'],
  imports: [
    CommonModule,
    IonicModule,
    MatMenuModule,
    TidyUiComponentsModule,
    EscapeHtmlPipe,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class OwnerBlockCardComponent implements OnDestroy {
  private readonly windowService = inject(WindowService);
  private readonly ownerBlocks = inject(OwnerBlocksService);
  private readonly schedule = inject(Schedule);
  private readonly demoBlock = inject(DemoBlockService);
  private readonly util = inject(Util);
  private readonly navCtrl = inject(CustomNavController);
  private readonly rightSidePanelService = inject(RightSidePanelService);
  private readonly storage = inject(TidyStorage);

  @Input() block!: OwnerBlock;
  @Input() size: OwnerBlockCardSize = 'sm';
  @Input() variant: OwnerBlockCardVariant = 'card';
  @Input() showLabel = true;
  @Input() address?: string;
  // Lets a host showing a snapshot of the schedule (the day summary panel)
  // drop the block, since the schedule's own reload doesn't reach it.
  @Output() unblocked = new EventEmitter<OwnerBlock>();

  @ViewChild(MatMenuTrigger) trigger?: MatMenuTrigger;

  isUnblocking = false;
  private openTimer: ReturnType<typeof setTimeout> | null = null;
  private closeTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly HOVER_OPEN_DELAY = 120;
  private readonly HOVER_CLOSE_DELAY = 180;

  ngOnDestroy(): void {
    this.clearTimers();
  }

  get isDesktop(): boolean {
    return this.windowService.isDesktopRes;
  }

  get nightsLabel(): string {
    const n = this.block?.nights;
    return n ? `${n}n` : '';
  }

  get nightsLongLabel(): string {
    const n = this.block?.nights;
    if (!n) { return ''; }
    return n === 1 ? '1 night' : `${n} nights`;
  }

  private clearTimers(): void {
    if (this.openTimer) {
      clearTimeout(this.openTimer);
      this.openTimer = null;
    }
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  onCardMouseEnter(): void {
    if (!this.isDesktop) {return;}
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
    if (this.trigger?.menuOpen) {return;}
    this.openTimer = setTimeout(() => {
      this.openTimer = null;
      this.trigger?.openMenu();
    }, this.HOVER_OPEN_DELAY);
  }

  onCardMouseLeave(): void {
    if (!this.isDesktop) {return;}
    if (this.openTimer) {
      clearTimeout(this.openTimer);
      this.openTimer = null;
    }
    this.scheduleClose();
  }

  private scheduleClose(): void {
    if (this.closeTimer) {return;}
    this.closeTimer = setTimeout(() => {
      this.closeTimer = null;
      this.trigger?.closeMenu();
    }, this.HOVER_CLOSE_DELAY);
  }

  onPopoverMouseEnter(): void {
    if (!this.isDesktop) {return;}
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  onPopoverMouseLeave(): void {
    if (!this.isDesktop) {return;}
    this.scheduleClose();
  }

  onCardClick(event: Event): void {
    event.stopPropagation();
    this.trigger?.openMenu();
  }

  /** Opens the Block Dates page on this block, to change its dates or reason. */
  async edit(): Promise<void> {
    this.trigger?.closeMenu();
    if (this.demoBlock.block()) { return; }
    const context: OwnerBlockEditContext = {
      addressId: this.block.addressId,
      startDate: this.block.period.startDate,
      endDate: this.block.period.endDate,
      reason: this.block.reason ?? null,
    };
    await this.storage.save(OWNER_BLOCK_EDIT_KEY, context);
    if (this.isDesktop) {
      await this.storage.save('dialog-right-side-open', true);
      this.rightSidePanelService.setDialogPageTitle('Edit Blocked Dates');
      this.rightSidePanelService.openRightSidePanel(AddOwnerBlockPage);
    } else {
      await this.storage.save('owner-block-return-page', 'schedule');
      await this.storage.save('dialog-right-side-open', false);
      this.navCtrl.navigateForward('add-owner-block');
    }
  }

  async unblock(): Promise<void> {
    this.trigger?.closeMenu();
    if (this.demoBlock.block()) { return; }
    const alert = await this.util.showConfirmAlert({
      header: 'Unblock these dates?',
      message: `${this.block.title} will become bookable again.`,
    });
    const { role } = await alert.onDidDismiss();
    if (role !== 'confirm') { return; }
    this.isUnblocking = true;
    const loading = await this.util.showLoading();
    try {
      await this.ownerBlocks.unblockRange(
        this.block.addressId,
        this.block.period.startDate,
        this.block.period.endDate
      );
      loading.dismiss();
      this.util.showSuccess('Dates unblocked');
      this.unblocked.emit(this.block);
      localStorage.setItem('madeUpdateToSchedule', 'true');
      this.schedule.setReloadSchedulePage();
    } catch (_error) {
      loading.dismiss();
      this.util.showError('Error unblocking these dates, please try again.');
    }
    this.isUnblocking = false;
  }
}
